import React from 'react';

import { useAuth } from '../contexts/AuthContext';
import { WebSocketProvider } from '../contexts/WebSocketContext';
import { getAuthToken } from '../services/authTokenStore';

interface WebSocketWrapperProps {
  children: React.ReactNode;
}

/**
 * Wrapper component that connects WebSocketProvider with AuthContext
 * Passes the authentication token to WebSocket for connection
 */
export function WebSocketWrapper({ children }: WebSocketWrapperProps) {
  const { token } = useAuth();
  const effectiveToken =
    token ||
    (typeof window !== 'undefined' ? getAuthToken() || undefined : undefined);

  // Use the correct WebSocket URL for the main-service
  // Build URL based on current location to support both dev and production
  const getWsUrl = () => {
    // Check environment variables first
    const envWsUrl =
      import.meta.env?.VITE_WS_URL ||
      (typeof process !== 'undefined' && process.env?.REACT_APP_WS_URL);
    if (envWsUrl) {
      // If running from a non-localhost host, ignore localhost WS URLs
      if (
        typeof window !== 'undefined' &&
        window.location.hostname &&
        window.location.hostname !== 'localhost' &&
        envWsUrl.includes('localhost')
      ) {
        // fall through to compute from current location
      } else {
        return envWsUrl;
      }
    }

    // Build URL from current location — use relative path for tunnel support
    // This ensures WebSocket goes through the same tunnel/port as the page
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host; // includes hostname:port

    // Always use current host:port (works with tunnels like wormhole)
    // Vite dev server will proxy /api/v1/ws to backend (8000)
    return `${protocol}//${host}/api/v1/ws`;
  };

  const wsUrl = getWsUrl();

  return (
    <WebSocketProvider
      url={wsUrl}
      token={effectiveToken}
      autoConnect={!!effectiveToken} // Auto-connect when token is available
    >
      {children}
    </WebSocketProvider>
  );
}
